import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Box, IconButton, TextField, InputAdornment, Tooltip, Chip } from "@mui/material";
import {
  Search,
  Download,
  KeyboardArrowDown,
  Pause,
  PlayArrow,
  Clear,
} from "@mui/icons-material";

import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { SearchAddon } from "@xterm/addon-search";
import { WebLinksAddon } from "@xterm/addon-web-links";
import "@xterm/xterm/css/xterm.css";

const TERMINAL_THEME = {
  background: "#1a1a2e",
  foreground: "#e6e6e6",
  cursor: "#4fc3f7",
  cursorAccent: "#1a1a2e",
  selection: "rgba(79, 195, 247, 0.3)",
  black: "#1a1a2e",
  red: "#ff6b6b",
  green: "#69f0ae",
  yellow: "#ffd93d",
  blue: "#4fc3f7",
  magenta: "#ba68c8",
  cyan: "#4dd0e1",
  white: "#e6e6e6",
  brightBlack: "#4a4a5e",
  brightRed: "#ff8a80",
  brightGreen: "#b9f6ca",
  brightYellow: "#ffff8d",
  brightBlue: "#80d8ff",
  brightMagenta: "#ea80fc",
  brightCyan: "#84ffff",
  brightWhite: "#ffffff",
};

export default function TerminalLog({
  logs = [],
  isStreaming = false,
  onClear,
  buildName,
  height = "400px",
}) {
  const terminalRef = useRef(null);
  const terminalInstance = useRef(null);
  const fitAddon = useRef(null);
  const searchAddon = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const lastLogCount = useRef(0);

  // Initialize terminal
  useEffect(() => {
    if (!terminalRef.current) return;

    // Don't reinitialize if already exists
    if (terminalInstance.current) return;

    // Check if container has dimensions
    const container = terminalRef.current;
    if (!container.offsetWidth || !container.offsetHeight) {
      // Wait for container to have dimensions
      const checkDimensions = setInterval(() => {
        if (container.offsetWidth && container.offsetHeight) {
          clearInterval(checkDimensions);
          initTerminal();
        }
      }, 100);

      // Cleanup interval after 3 seconds
      setTimeout(() => clearInterval(checkDimensions), 3000);
      return;
    }

    initTerminal();

    function initTerminal() {
      if (terminalInstance.current) return;

      const term = new Terminal({
        theme: TERMINAL_THEME,
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        fontSize: 13,
        lineHeight: 1.4,
        cursorBlink: false,
        cursorStyle: "bar",
        scrollback: 10000,
        convertEol: true,
        disableStdin: true,
      });

      fitAddon.current = new FitAddon();
      searchAddon.current = new SearchAddon();

      term.loadAddon(fitAddon.current);
      term.loadAddon(searchAddon.current);
      term.loadAddon(new WebLinksAddon());

      term.open(container);

      // Delay fit to ensure DOM is ready
      requestAnimationFrame(() => {
        try {
          if (fitAddon.current && container.offsetWidth && container.offsetHeight) {
            fitAddon.current.fit();
          }
        } catch (e) {
          console.warn('Terminal fit error:', e);
        }
      });

      // Handle scroll events to detect user scroll
      term.onScroll(() => {
        const buffer = term.buffer.active;
        const isAtBottom = buffer.viewportY >= buffer.baseY - 1;
        if (!isAtBottom && autoScroll) {
          setAutoScroll(false);
        }
      });

      terminalInstance.current = term;
    }

    // Handle resize
    const handleResize = () => {
      if (fitAddon.current && terminalRef.current?.offsetWidth) {
        try {
          fitAddon.current.fit();
        } catch (e) {
          console.warn('Terminal fit error on resize:', e);
        }
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (terminalInstance.current) {
        terminalInstance.current.dispose();
        terminalInstance.current = null;
      }
    };
  }, []);

  // Track previous logs reference to detect filter changes
  const prevLogsRef = useRef(logs);

  // Write logs to terminal
  useEffect(() => {
    if (!terminalInstance.current) return;

    const term = terminalInstance.current;

    // Clear and rewrite if logs array reference changed (filter applied) or length decreased
    const logsChanged = prevLogsRef.current !== logs;
    const shouldClear = logs.length === 0 || logs.length < lastLogCount.current || logsChanged;

    if (shouldClear) {
      term.clear();
      lastLogCount.current = 0;
    }

    prevLogsRef.current = logs;

    const newLogs = logs.slice(lastLogCount.current);

    newLogs.forEach((log) => {
      const line = typeof log === "string" ? log : log.line || log.message || "";
      term.writeln(formatLogLine(line, log.step));
    });

    lastLogCount.current = logs.length;

    if (autoScroll) {
      term.scrollToBottom();
    }
  }, [logs, autoScroll]);

  // Format log line with step prefix and colors
  const formatLogLine = (line, step) => {
    let prefix = "";
    if (step) {
      const stepColors = {
        PREPARE: "\x1b[36m", // Cyan
        DETECT: "\x1b[33m", // Yellow
        ANALYZE: "\x1b[35m", // Magenta
        RESTORE: "\x1b[34m", // Blue
        BUILD: "\x1b[32m", // Green
        EXPORT: "\x1b[36m", // Cyan
      };
      const color = stepColors[step] || "\x1b[37m";
      prefix = `${color}[${step}]\x1b[0m `;
    }
    return prefix + line;
  };

  // Search functionality
  const handleSearch = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (searchAddon.current && query) {
      searchAddon.current.findNext(query, { incremental: true });
    }
  }, []);

  const handleSearchNext = useCallback(() => {
    if (searchAddon.current && searchQuery) {
      searchAddon.current.findNext(searchQuery);
    }
  }, [searchQuery]);

  const handleSearchPrev = useCallback(() => {
    if (searchAddon.current && searchQuery) {
      searchAddon.current.findPrevious(searchQuery);
    }
  }, [searchQuery]);

  // Download logs
  const handleDownload = useCallback(() => {
    const content = logs
      .map((log) => (typeof log === "string" ? log : log.line || log.message || ""))
      .join("\n");

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${buildName || "build"}-logs.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [logs, buildName]);

  // Clear terminal
  const handleClear = useCallback(() => {
    if (terminalInstance.current) {
      terminalInstance.current.clear();
      lastLogCount.current = 0;
    }
    onClear?.();
  }, [onClear]);

  // Toggle auto-scroll
  const toggleAutoScroll = useCallback(() => {
    setAutoScroll((prev) => {
      if (!prev && terminalInstance.current) {
        terminalInstance.current.scrollToBottom();
      }
      return !prev;
    });
  }, []);

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: height,
        borderRadius: "16px",
        overflow: "hidden",
        background: TERMINAL_THEME.background,
        boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: "rgba(255,255,255,0.05)",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isStreaming && (
            <Chip
              size="small"
              label="LIVE"
              sx={{
                background: "rgba(105, 240, 174, 0.2)",
                color: "#69f0ae",
                fontWeight: 600,
                fontSize: "0.7rem",
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                },
              }}
            />
          )}
          <Chip
            size="small"
            label={`${logs.length} lines`}
            sx={{
              background: "rgba(255,255,255,0.1)",
              color: "rgba(255,255,255,0.7)",
              fontSize: "0.7rem",
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {showSearch && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: "auto", opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
            >
              <TextField
                size="small"
                placeholder="Search..."
                value={searchQuery}
                onChange={handleSearch}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.shiftKey ? handleSearchPrev() : handleSearchNext();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "rgba(255,255,255,0.5)", fontSize: "1rem" }} />
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: "0.8rem",
                    color: "#fff",
                    background: "rgba(255,255,255,0.05)",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid rgba(255,255,255,0.1)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.2)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#4fc3f7",
                    },
                  },
                }}
                sx={{ width: "200px" }}
              />
            </motion.div>
          )}

          <Tooltip title="Search">
            <IconButton
              size="small"
              onClick={() => setShowSearch(!showSearch)}
              sx={{ color: showSearch ? "#4fc3f7" : "rgba(255,255,255,0.5)" }}
            >
              <Search fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={autoScroll ? "Pause auto-scroll" : "Resume auto-scroll"}>
            <IconButton
              size="small"
              onClick={toggleAutoScroll}
              sx={{ color: autoScroll ? "#69f0ae" : "rgba(255,255,255,0.5)" }}
            >
              {autoScroll ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Scroll to bottom">
            <IconButton
              size="small"
              onClick={() => terminalInstance.current?.scrollToBottom()}
              sx={{ color: "rgba(255,255,255,0.5)" }}
            >
              <KeyboardArrowDown fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Clear">
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{ color: "rgba(255,255,255,0.5)" }}
            >
              <Clear fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Download logs">
            <IconButton
              size="small"
              onClick={handleDownload}
              sx={{ color: "rgba(255,255,255,0.5)" }}
            >
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Terminal */}
      <Box
        ref={terminalRef}
        sx={{
          flex: 1,
          padding: "8px",
          "& .xterm": {
            height: "100%",
          },
          "& .xterm-viewport": {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(255,255,255,0.2)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
          },
        }}
      />
    </Box>
  );
}
